/**
 * Hook for fetching comparison period data
 */

import React, { useEffect } from 'react';
import { getDailyProfitRange, DailyProfitSummary } from '../api/profit';
import { useComparison, useComparisonStore } from '../stores/useComparisonStore';
import { useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';

/**
 * Comparison data fetch result
 */
export interface ComparisonDataResult {
  data: DailyProfitSummary[] | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch comparison period data
 * Automatically fetches data when comparison is activated
 * Refetches on auto-refresh trigger
 * 
 * @returns Comparison data result with loading and error states
 */
export function useComparisonData(): ComparisonDataResult {
  const { active, range, data } = useComparison();
  const setComparisonData = useComparisonStore((state) => state.setComparisonData);
  const refreshTrigger = useAutoRefreshTrigger('profit');
  
  // Track loading and error states locally
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    // Only fetch if comparison is active and we have a range
    if (!active || !range) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const comparisonData = await getDailyProfitRange(
          range.start_date,
          range.end_date,
          undefined // No product filter
        );
        
        setComparisonData(comparisonData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching comparison data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch comparison data');
        setLoading(false);
      }
    };

    fetchData();
  }, [active, range?.start_date, range?.end_date, refreshTrigger, setComparisonData]);

  return {
    data,
    loading,
    error
  };
}
